import React from 'react';
import styled from 'styled-components/macro';
import { PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import WidgetHeader from '../dashboard/main/widgets/common/WidgetHeader';
import CustomTooltip from '../dashboard/main/tooltip/CustomTooltip';
import { EmptyWidget } from '../dashboard/main/EmptyWidget';
import CustomLegend from './CustomLegend';
import { Flex } from 'lib/components/styles/layout';
import {
  ParagraphSmall,
  ParagraphSmallBold,
} from 'lib/components/styles/typography';
import { LoadingIndicator } from 'lib/components';
import { IMapperData } from './types';

const StatsWrapper = styled.div`
  display: flex;
  gap: 16px;
`;
const RecordedWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
const RecordedNumber = styled.p`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  color: #272a32;
`;
const RecordedDesc = styled.p`
  color: #9297a2;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
`;

const COLORS = ['#001B53', '#B6E0EC'];

const getPercentage = (num: number, denom: number): string => {
  return denom > 0 ? ((num / denom) * 100).toFixed(2) : '0.00';
};

const RoiConversionRateWidget = ({
  conversionRateWithViews,
  conversionRateWithoutViews,
  count,
  totalSold,
  isLoading,
}: {
  conversionRateWithViews: number;
  conversionRateWithoutViews: number;
  count: number;
  totalSold: number;
  isLoading: boolean;
}) => {
  const widgetData: IMapperData = {
    data: [
      {
        id: 1,
        name: 'Vehicles Sold W/ Video',
        value: conversionRateWithViews,
        color: COLORS[0],
      },
      {
        id: 2,
        name: 'Vehicles Sold W/O Video',
        value: conversionRateWithoutViews,
        color: COLORS[1],
      },
    ],
    total: totalSold,
  };

  const { data, total } = widgetData;

  if (isLoading) {
    return (
      <Flex>
        <LoadingIndicator isLoading={isLoading} height={260} />
      </Flex>
    );
  }

  return (
    <Flex>
      <WidgetHeader title='Conversion Rate' isRefetching={false}>
        <StatsWrapper>
          <RecordedWrapper>
            <RecordedNumber>{total}</RecordedNumber>
            <RecordedDesc>vehicle{total !== 1 ? 's' : ''} sold</RecordedDesc>
          </RecordedWrapper>
        </StatsWrapper>
      </WidgetHeader>

      {total > 0 ? (
        <div>
          <PieChart width={400} height={185}>
            <Pie
              data={data}
              dataKey='value'
              nameKey='name'
              innerRadius={30}
              outerRadius={60}
              cx={65}
              cy={75}
            >
              {data.map((entry, index) => (
                <Cell key={entry.id} fill={COLORS[index]} />
              ))}
            </Pie>
            <Legend
              content={<CustomLegend data={data} />}
              layout='vertical'
              verticalAlign='middle'
              align='right'
            />
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
          <Flex width='100%' justifyContent='space-between' flexDirection='row'>
            <ParagraphSmall>Conversion Rate W/ Video</ParagraphSmall>
            <ParagraphSmallBold>
              {getPercentage(conversionRateWithViews, count)} %
            </ParagraphSmallBold>
          </Flex>
          <Flex width='100%' justifyContent='space-between' flexDirection='row'>
            <ParagraphSmall>Conversion Rate W/O Video</ParagraphSmall>
            <ParagraphSmallBold>
              {getPercentage(conversionRateWithoutViews, count)} %
            </ParagraphSmallBold>
          </Flex>
        </div>
      ) : (
        <EmptyWidget />
      )}
    </Flex>
  );
};

export default RoiConversionRateWidget;
